import { APP_BASE_HREF, DOCUMENT } from '@angular/common';
import {
  AfterContentChecked,
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  CombinedSearchResult,
  DataAccessExhibitorsFacade,
  DataAccessFairsFacade,
  ExhibitorDirectoryStatus,
  FairConfiguration,
} from '@planone/bau365/data-access-fair-data';
import { DataAccessScreensizeFacade } from '@planone/bau365/data-access-screensize';
import { BreadcrumbHelper } from '@planone/bau365/feature-breadcrumb';
import { EMPTY_SEARCH_REGEX } from '@planone/bau365/util-search';
import { DataAccessTogglesFacade } from '@planone/shared/data-access-feature-flags';
import { BreadcrumbElement } from '@planone/shared/ui-elements';
import { DocumentRef } from '@planone/shared/util-browser-api';
import { localizedMeta, MetaService } from '@planone/shared/util-meta';
import { BreadcrumbGenerator } from '@planone/shared/util-structured-data';
import { KeycloakService } from 'keycloak-angular';
import {
  combineLatest,
  combineLatestWith,
  delay,
  filter,
  map,
  Observable,
  Subject,
  Subscription,
  takeUntil,
  withLatestFrom,
} from 'rxjs';

@Component({
  selector: 'bau365-page-exhibitor-directory',
  templateUrl: './page-exhibitor-directory.component.html',
  styleUrls: ['./page-exhibitor-directory.component.scss'],
})
export class PageExhibitorDirectoryComponent implements OnInit, OnDestroy, AfterContentChecked {
  exhibitorDirectory$: Observable<CombinedSearchResult[]>;
  exhibitorDirectoryStatus$: Observable<ExhibitorDirectoryStatus>;
  exhibitorDirectoryHallList$: Observable<string[]>;
  exhibitorDirectoryLetterList$: Observable<string[]>;
  isBauInsights$: Observable<boolean>;
  fairConfig$: Observable<FairConfiguration>;
  size$: Observable<string | undefined>;
  watchlistEnabled$: Observable<boolean | undefined>;

  sortIndex: string[] = [];
  fairId: string | undefined = undefined;
  isLoggedIn = false;
  tab = 'exhibitor';
  activeLetter = '';
  firstAvailableHall: string | undefined;
  initialScrollPositionReached: boolean | undefined;
  scrollPosition: number | undefined;
  activeSearch = false;
  searchTerm = '';
  searchPlaceholderText: string | undefined;
  loadedSearchResults: number | undefined;
  totalSearchResults: number | undefined;
  breadcrumbElements: BreadcrumbElement[] | undefined;

  private unsubscribe: Subject<void> = new Subject<void>();
  private directorySubscription?: Subscription;
  private placeholderHeight?: number = undefined;
  private scrollPoints: number[] = [];

  constructor(
    private ExhibitorsFacade: DataAccessExhibitorsFacade,
    private documentRef: DocumentRef,
    private renderer: Renderer2,
    private el: ElementRef,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private keycloakService: KeycloakService,
    private metaService: MetaService,
    private structuredDataGenerator: BreadcrumbGenerator,
    private breadcrumbHelper: BreadcrumbHelper,
    private renderer2: Renderer2,
    private fairsFacade: DataAccessFairsFacade,
    togglesFacade: DataAccessTogglesFacade,
    FairsFacade: DataAccessFairsFacade,
    ScreensizeFacade: DataAccessScreensizeFacade,
    @Inject(DOCUMENT) private document: Document,
    @Inject(APP_BASE_HREF) private baseHref: string,
    @Inject(EMPTY_SEARCH_REGEX) private emptySearchRegex: RegExp
  ) {
    this.metaService.setTitle(localizedMeta['titleExhibitorDirectory']);

    this.exhibitorDirectory$ = ExhibitorsFacade.exhibitorDirectoryResults$.pipe(delay(10));
    this.exhibitorDirectoryStatus$ = ExhibitorsFacade.exhibitorDirectoryStatus$.pipe(delay(10));
    this.exhibitorDirectoryHallList$ = ExhibitorsFacade.exhibitorDirectoryHallList$;
    this.exhibitorDirectoryLetterList$ = ExhibitorsFacade.exhibitorDirectoryLetterList$;
    this.isBauInsights$ = FairsFacade.isBauInsights$;
    this.fairConfig$ = FairsFacade.fairConfig$;
    this.size$ = ScreensizeFacade.size$;
    this.watchlistEnabled$ = togglesFacade.flag('kill-watchlist').pipe(map((watchlistDisabled) => !watchlistDisabled));
  }

  ngOnInit(): void {
    combineLatest([this.activatedRoute.params, this.activatedRoute.queryParams])
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(([params, queryParams]) => {
        if (!queryParams['searchTerm'] || this.searchTerm === '') {
          this.ExhibitorsFacade.directoryPageOpenedAnywhere(params['fair_id']);
        }

        this.fairId = params['fair_id'];
        this.tab = queryParams['hallId'] ? 'hall' : 'exhibitor';

        if (queryParams['tab'] === 'hall') {
          this.tab = 'hall';
        }

        const searchTerm = queryParams['searchTerm'];

        if (searchTerm) {
          this.activeSearch = true;
          this.searchTerm = searchTerm;
          this.ExhibitorsFacade.directorySearchPageOpened(searchTerm, params['fair_id']);
        } else if (queryParams['hallId'] && !searchTerm) {
          this.activeLetter = queryParams['hallId'];
          this.ExhibitorsFacade.directoryHallsPageOpened(queryParams['hallId'], params['fair_id']);
        } else if (queryParams['character'] && !searchTerm) {
          this.activeLetter = queryParams['character'];
          this.ExhibitorsFacade.directoryPageOpened(queryParams['character'], params['fair_id']);
        }
      });

    this.activatedRoute.queryParams
      .pipe(
        takeUntil(this.unsubscribe),
        filter((params) => params['scrollposition'])
      )
      .subscribe((params) => {
        this.scrollPosition = params['scrollposition'];
      });

    this.isBauInsights$
      .pipe(combineLatestWith(this.fairsFacade.fairConfig$), takeUntil(this.unsubscribe))
      .subscribe(([isBauInsights, fair]) => {
        this.metaService.removeOpenGraphMetaTags();

        if (isBauInsights) {
          this.metaService.addOpenGraphMetaTags(
            localizedMeta['linkPreviewTitleCompanyDirectory'],
            localizedMeta['linkPreviewDescriptionCompanyDirectory'],
            this.document.URL,
            this.baseHref + 'assets/link-preview-img.png',
            [fair.name]
          );
        } else {
          this.metaService.addOpenGraphMetaTags(
            localizedMeta['linkPreviewTitleExhibitorDirectory'],
            localizedMeta['linkPreviewDescriptionExhibitorDirectory'],
            this.document.URL,
            this.baseHref + 'assets/link-preview-img.png',
            [fair.name]
          );
        }
      });

    this.isBauInsights$.pipe(takeUntil(this.unsubscribe)).subscribe((isBauInsights) => {
      if (isBauInsights) {
        this.metaService.setTitle(localizedMeta['titleCompanyDirectory']);
      }

      this.searchPlaceholderText = isBauInsights
        ? $localize`:@@company-directory-search-placeholder:Unternehmen eingeben und suchen`
        : $localize`:@@exhibitor-directory-search-placeholder:Aussteller eingeben und suchen`;
    });

    this.exhibitorDirectoryHallList$.pipe(takeUntil(this.unsubscribe)).subscribe((hallList: string[]) => {
      this.firstAvailableHall = hallList?.length > 0 ? hallList[0] : undefined;

      if (this.tab === 'hall') {
        this.initDirectory(hallList);
      }
    });

    this.exhibitorDirectoryLetterList$.pipe(takeUntil(this.unsubscribe)).subscribe((letterList: string[]) => {
      if (this.tab === 'exhibitor') {
        this.initDirectory(letterList);
      }
    });

    this.exhibitorDirectoryStatus$.pipe(takeUntil(this.unsubscribe)).subscribe((directoryStatus) => {
      this.loadedSearchResults = directoryStatus.searchStatus?.loadedResults;
      this.totalSearchResults = directoryStatus.searchStatus?.count;
    });

    this.isLoggedIn = this.keycloakService.isLoggedIn();

    this.exhibitorDirectory$
      .pipe(takeUntil(this.unsubscribe), withLatestFrom(this.fairsFacade.fairConfig$))
      .subscribe(([_, fair]) => {
        let breadcrumbData;
        if (this.searchTerm && this.searchTerm !== '') {
          breadcrumbData = this.breadcrumbHelper.prepareForExhibitorSearch(fair);
        } else {
          breadcrumbData = this.breadcrumbHelper.prepareForExhibitorDirectory(
            fair,
            this.activeLetter,
            this.firstAvailableHall
          );
        }
        this.breadcrumbElements = breadcrumbData.elements;
        this.structuredDataGenerator.validateBreadcrumbStructuredData(
          this.renderer2,
          breadcrumbData.structuredData,
          this.document.head
        );
      });
  }

  ngAfterContentChecked() {
    if (!this.initialScrollPositionReached && this.scrollPosition) {
      window.scrollTo(0, this.scrollPosition);
      this.initialScrollPositionReached = Math.trunc(window.scrollY) > this.scrollPosition - 10;
    }
  }

  triggerSearch(searchTerm: string) {
    if (this.emptySearchRegex.test(searchTerm)) return;

    if (searchTerm) {
      this.router.navigate(['fairs', this.fairId, 'exhibitors', 'search'], {
        queryParams: {
          searchTerm: searchTerm !== '' ? searchTerm : null,
          tab: this.tab === 'exhibitor' ? 'exhibitor' : 'hall',
          page: null,
          searchTypes: null,
          sort: null,
          filter: null,
          dateFilter: null,
        },
        queryParamsHandling: 'merge',
      });
    } else {
      this.router.navigate(['fairs', this.fairId, 'exhibitors', 'letters'], {
        queryParams: {
          tab: this.tab === 'exhibitor' ? 'exhibitor' : 'hall',
          ...(this.tab === 'exhibitor' && { character: this.activatedRoute.snapshot.queryParamMap.get('character') }),
          ...(this.tab === 'hall' && { hallId: this.activatedRoute.snapshot.queryParamMap.get('hallId') }),
        },
      });
    }
  }

  loadMoreSearchResults() {
    this.ExhibitorsFacade.directorySearchLoadNext(this.searchTerm, this.fairId ?? 'unknown');
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    const isSticky = this.el.nativeElement.getAttribute('sticky') ?? 'false';

    const scrollPoints = this.documentRef.nativeDocument.getElementsByClassName('result');
    let scrollPointTop = 0;

    for (const scrollPoint in scrollPoints) {
      if (typeof scrollPoints[scrollPoint] === 'object') {
        const before = this.scrollPoints[scrollPoint];
        const boundingRect = scrollPoints[scrollPoint].getBoundingClientRect();
        this.scrollPoints[scrollPoint] = boundingRect.top;

        if (boundingRect.top !== before) {
          scrollPointTop = boundingRect.top;
        }
      }
    }

    if (isSticky === 'false') {
      this.placeholderHeight = this.documentRef.nativeDocument.querySelector<HTMLElement>('.selector')?.offsetHeight;
    }

    const placeholders = this.documentRef.nativeDocument.getElementsByClassName('selector-placeholder');
    for (const placeholder in placeholders) {
      const placeholderElement = placeholders[placeholder];

      if (typeof placeholderElement === 'object') {
        this.renderer.setStyle(placeholderElement, 'height', this.placeholderHeight + 'px');
      }
    }

    if (scrollPointTop < (this.placeholderHeight ?? 0)) {
      this.renderer.setAttribute(this.el.nativeElement, 'sticky', 'true');
    } else {
      this.renderer.setAttribute(this.el.nativeElement, 'sticky', 'false');
    }
  }

  ngOnDestroy() {
    this.directorySubscription?.unsubscribe();
    this.metaService.removeOpenGraphMetaTags();
    this.structuredDataGenerator.removeTag(this.renderer2, this.document.head);

    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  private initDirectory(list: string[] = []): void {
    this.sortIndex = [];

    for (const char of list) {
      this.sortIndex.push(char);
    }
  }

  trackById = (index: number, item: CombinedSearchResult) =>
    item.type === 'EXHIBITOR' ? item.exhibitor.id : item.type === 'BRAND' ? item.brand.id : 'index' + index;

  trackByIndex = (index: number, _selector: string) => index;
}
